import { useEffect, useRef } from 'react';
import { useChatDock } from '@/app/providers/ChatDockContext';
import { useBriefDock } from '@/app/providers/BriefDockContext';
import { useReviseSession } from '@/app/providers/ReviseSessionContext';

/**
 * Chat ↔ notifications mutual-exclusivity coordinator (Workstream 6). Enforces STRICT single-open,
 * reacting to state TRANSITIONS so programmatic opens (toasts, automation cards) are covered:
 *   • notifications open → dockChat()   • chat opens → closeBrief()
 * The sole exception is an active Revise session — while `reviseActive` the coordinator YIELDS.
 * Called once from MainLayout.
 *
 * Edge + pending-intent design (why this isn't a plain "on open, close the other"):
 * Both context methods guard `if (dockState !== 'open') return` (ChatDockContext.dockChat,
 * BriefDockContext.closeBrief). If the TARGET panel is mid-flight ('undocking', ~230–290ms under
 * animated motion), reacting to the open-edge with dockChat()/closeBrief() is SILENTLY rejected —
 * the target then settles 'open' WITHOUT crossing an `*Opening` boolean edge ('undocking'→'open'
 * keeps the boolean true), so a boolean-only effect never retries and BOTH panels settle open
 * outside a Revise session (reachable by two quick clicks or a programmatic openBrief from a toast).
 *
 * So we REMEMBER the action the edge demands (`desiredRef`) and execute it once the target settles:
 *  - The raw `chatDockState`/`briefDockState` are in the deps, so the settle transition
 *    ('undocking'→'open') re-runs the effect even though the booleans don't change.
 *  - A Revise session blesses both-open: on the yield we DISCARD any stale intent, and because the
 *    prev-refs are updated even while yielding, `endRevise()` fires no edge — both panels stay open
 *    (no retro-close). That invariant is pinned by tests.
 *  - The intent is also dropped when it becomes moot (the other panel went away) or is satisfied
 *    (the target reached a non-opening state), and the target methods are idempotent, so a settled
 *    action is never re-issued.
 *
 * No loop: dockChat → 'docking'/'docked' (not "opening"); closeBrief → 'docking'/'closed' (not
 * "opening"), each terminating.
 */
export function useChatNotificationExclusivity(): void {
  const { dockState: chatDockState, dockChat } = useChatDock();
  const { dockState: briefDockState, closeBrief } = useBriefDock();
  const { reviseActive } = useReviseSession();

  const chatOpening = chatDockState === 'open' || chatDockState === 'undocking';
  const briefOpening = briefDockState === 'open' || briefDockState === 'undocking';

  const prevChatOpeningRef = useRef(chatOpening);
  const prevBriefOpeningRef = useRef(briefOpening);
  // The action recorded at the last open-edge, kept alive across a mid-flight target guard-rejection.
  const desiredRef = useRef<null | 'dockChat' | 'closeBrief'>(null);

  useEffect(() => {
    const chatJustOpened = chatOpening && !prevChatOpeningRef.current;
    const briefJustOpened = briefOpening && !prevBriefOpeningRef.current;
    // Track the latest edges even when we yield, so a Revise-session yield can't leave a stale edge.
    prevChatOpeningRef.current = chatOpening;
    prevBriefOpeningRef.current = briefOpening;

    // Record the enforcing action the edge demands (overwrite — the newest open wins).
    if (briefJustOpened && chatOpening) {
      desiredRef.current = 'dockChat';
    } else if (chatJustOpened && briefOpening) {
      desiredRef.current = 'closeBrief';
    }

    if (reviseActive) {
      // Revise is the sole both-open state — yield and discard any stale intent.
      desiredRef.current = null;
      return;
    }

    const desired = desiredRef.current;
    if (desired === 'dockChat') {
      if (!briefOpening) {
        desiredRef.current = null; // moot — the notifications panel went away
      } else if (chatDockState === 'open') {
        dockChat(); // target settled — dock it; clears on a later run once chat leaves 'open'
      } else if (!chatOpening) {
        desiredRef.current = null; // satisfied — chat is docked/docking
      }
      // else chat is 'undocking' — wait for it to settle, keep the intent
    } else if (desired === 'closeBrief') {
      if (!chatOpening) {
        desiredRef.current = null; // moot — the chat went away
      } else if (briefDockState === 'open') {
        closeBrief(); // target settled — close it; clears on a later run once brief leaves 'open'
      } else if (!briefOpening) {
        desiredRef.current = null; // satisfied — brief is closed/docking
      }
      // else brief is 'undocking' — wait for it to settle, keep the intent
    }
  }, [chatOpening, briefOpening, chatDockState, briefDockState, reviseActive, dockChat, closeBrief]);
}
